import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaArrowLeft, FaThumbsDown, FaThumbsUp } from 'react-icons/fa';

import { stocklistActions } from '../../../actions';
import LoadingImage from '../../../components/general/LoadingImage';
import { uiHelpers } from '../../../helpers';
import { useAnalytics } from '../../../helpers/analytics.helper';
import useDispatch from '../../../hooks/useDispatch';
import useRegion from '../../../hooks/useRegion';
import useSelector from '../../../hooks/useSelector';
import { useUserStockList } from '../../../hooks/useUserStockList';
import { Analytics } from '../../../shared/types';
import FeatureRequiresVIPOverlay from '../../FreeUser/FeatureRequiresVIPOverlay';
import TutorialQuestionMark from '../../Help/TutorialQuestionMark';
import { Styles } from './UserListBar.styles';

const UserListBar: React.FC = () => {
  const { view } = useSelector(state => state.stocklist);
  const { users, loading } = useUserStockList();
  const region = useRegion();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(stocklistActions.getUserStockList(region));
  }, [region, dispatch]);

  const { i18n, t } = useTranslation();

  const selectedUser = useMemo(
    () => (users ? users.find(user => user.userId === view.userId) : null),
    [users, view.userId]
  );

  const [queue] = useAnalytics();

  const toDropdownItem = useCallback(
    user => {
      const recentStock = user.stocks.length > 0 && user.stocks[0];
      const unlike = recentStock && user.unlikes[0];
      const recentSpan = recentStock && (
        <span>
          {unlike ? <FaThumbsDown /> : <FaThumbsUp />} {recentStock}
        </span>
      );
      return (
        <div
          className={classNames('user-list-item', {
            selected: user.userId === view.userId,
          })}
          key={user.userId}
          onClick={() => {
            dispatch(stocklistActions.setViewingUserId(user.userId));
            queue(Analytics.ActionEnum.ViewUserFavList, null, user.userId);
            setShowList(false);
          }}
        >
          <div className="user-name-container">
            <div className="user-name">{user.name}</div> ({user.stocks.length})
          </div>
          <span className="last-update-span">
            {uiHelpers.formatDeltaDateTime(i18n, user.updatedAt)} {recentSpan}
          </span>
        </div>
      );
    },
    [dispatch, i18n, queue, view.userId]
  );

  const [showList, setShowList] = useState(false);
  const [filterUsersInputValue, setFilterUsersInputValue] = useState('');

  const toggleList = useCallback(() => {
    if (!showList) {
      dispatch(stocklistActions.getUserStockList(region));
    }
    setShowList(showList => !showList);
  }, [dispatch, region, showList]);

  const vipUsers = (users || []).filter(
    ({ name, vip }) => name.toLowerCase().includes(filterUsersInputValue) && vip
  );

  const nonVipUsers = (users || []).filter(
    ({ name, vip }) =>
      name.toLowerCase().includes(filterUsersInputValue) && !vip
  );

  return (
    <Styles>
      <div
        className={classNames('toggle', { selected: Boolean(selectedUser) })}
      >
        <div onClick={toggleList}>
          {!selectedUser && t('usl-select')}
          {selectedUser && (
            <Trans
              i18nKey="usl-you-are-viewing"
              values={{ name: selectedUser.name }}
              components={{ u: <u /> }}
            />
          )}
        </div>
        <TutorialQuestionMark tutorialId={20} />
        {showList && (
          <div className="list-dropdown">
            <FeatureRequiresVIPOverlay
              featureTitle={t('feature-stocklist')}
              always
            >
              <LoadingImage loading={loading} />
              {view.userId && (
                <div
                  className="user-list-item align-left"
                  onClick={() => {
                    dispatch(stocklistActions.setViewingUserId(null));
                    setFilterUsersInputValue('');
                  }}
                >
                  <FaArrowLeft /> {t('usl-go-back')}
                </div>
              )}
              {users && (
                <>
                  <div className="disabled">
                    <i>
                      <Trans i18nKey="usl-explain1" />
                    </i>
                  </div>
                  <input
                    style={{ width: '90%', textAlign: 'center' }}
                    value={filterUsersInputValue}
                    placeholder={'輸入用户名稱'}
                    onChange={e => setFilterUsersInputValue(e.target.value)}
                  />
                  <div className="user-list-header">{t('usl-member')}</div>
                  <div className="user-list-section">
                    {vipUsers.length > 0
                      ? vipUsers.map(toDropdownItem)
                      : t('usl-not-found')}
                  </div>
                  <div className="user-list-header">{t('usl-free')}</div>
                  <div className="user-list-section">
                    {nonVipUsers.length > 0
                      ? nonVipUsers.map(toDropdownItem)
                      : t('usl-not-found')}
                  </div>
                </>
              )}
              <div className="disabled">
                <i>
                  <Trans i18nKey="usl-explain" />
                </i>
              </div>
            </FeatureRequiresVIPOverlay>
          </div>
        )}
      </div>
    </Styles>
  );
};

export default UserListBar;
